import { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
    CircularProgress,
} from "@mui/material";
import { supabase } from "../lib/supabaseClient";

const colors = {
    paper: "rgba(255, 250, 242, 0.94)",
    ink: "#2a2621",
    muted: "rgba(42, 38, 33, 0.66)",
    brown: "#9b7653",
    brownDark: "#6f5136",
    brownDarker: "#543b26",
    border: "rgba(155,118,83,0.16)",
    success: "#4f7b58",
};

const defaultForm = {
    active: false,
    variant: "info",
    title: "Aktueller Hinweis",
    message: "Hier kann ein wichtiger Hinweis für Besucher stehen.",
    button_label: "",
    button_url: "",
};

function cleanText(value) {
    return typeof value === "string" ? value.trim() : "";
}

function cleanNullableText(value) {
    const cleanedValue = cleanText(value);
    return cleanedValue || null;
}

function AdminSiteNoticeSettings() {
    const [form, setForm] = useState(defaultForm);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        async function loadNotice() {
            try {
                setIsLoading(true);
                setSuccessMessage("");
                setErrorMessage("");

                const { data, error } = await supabase
                    .from("site_notice")
                    .select("*")
                    .eq("id", "main")
                    .maybeSingle();

                if (error) throw error;

                if (!data) {
                    setForm(defaultForm);
                    return;
                }

                setForm({
                    active: Boolean(data.active),
                    variant: data.variant || "info",
                    title: data.title || defaultForm.title,
                    message: data.message || "",
                    button_label: data.button_label || "",
                    button_url: data.button_url || "",
                });
            } catch (error) {
                console.error("Site notice load error:", error);
                setErrorMessage(
                    error.message || "Hinweis konnte nicht geladen werden."
                );
            } finally {
                setIsLoading(false);
            }
        }

        loadNotice();
    }, []);

    function updateField(name, value) {
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    }

    async function handleSubmit(event) {
        event.preventDefault();

        if (!cleanText(form.title)) {
            setErrorMessage("Bitte einen Titel eingeben.");
            return;
        }

        try {
            setIsSubmitting(true);
            setSuccessMessage("");
            setErrorMessage("");

            const payload = {
                id: "main",
                active: Boolean(form.active),
                variant: cleanText(form.variant) || "info",
                title: cleanText(form.title),
                message: cleanNullableText(form.message),
                button_label: cleanNullableText(form.button_label),
                button_url: cleanNullableText(form.button_url),
                updated_at: new Date().toISOString(),
            };

            const { data: existingData, error: existingError } = await supabase
                .from("site_notice")
                .select("id")
                .eq("id", "main")
                .maybeSingle();

            if (existingError) throw existingError;

            if (existingData) {
                const { error } = await supabase
                    .from("site_notice")
                    .update(payload)
                    .eq("id", "main");

                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from("site_notice")
                    .insert(payload);

                if (error) throw error;
            }

            setSuccessMessage("Website-Hinweis wurde gespeichert.");
        } catch (error) {
            console.error("Site notice save error:", error);
            setErrorMessage(
                error.message || "Website-Hinweis konnte nicht gespeichert werden."
            );
        } finally {
            setIsSubmitting(false);
        }
    }

    if (isLoading) {
        return (
            <Paper
                elevation={0}
                sx={{
                    py: 8,
                    px: 3,
                    borderRadius: 5,
                    backgroundColor: colors.paper,
                    border: `1px solid ${colors.border}`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                }}
            >
                <CircularProgress sx={{ color: colors.brownDark }} />

                <Typography
                    sx={{
                        fontFamily: '"Nunito Sans", sans-serif',
                        fontWeight: 800,
                        color: colors.muted,
                    }}
                >
                    Website-Hinweis wird geladen...
                </Typography>
            </Paper>
        );
    }

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Paper
                elevation={0}
                sx={{
                    mb: 3,
                    p: {
                        xs: 3,
                        md: 5,
                    },
                    borderRadius: {
                        xs: 4,
                        md: 6,
                    },
                    border: `1px solid ${colors.border}`,
                    background:
                        "radial-gradient(circle at 88% 14%, rgba(155,118,83,0.18) 0%, rgba(155,118,83,0) 30%), linear-gradient(145deg, rgba(255,250,242,0.96) 0%, rgba(247,241,232,0.92) 100%)",
                    boxShadow: "0 24px 80px rgba(78,54,30,0.1)",
                }}
            >
                <Typography
                    sx={{
                        mb: 1,
                        fontFamily: '"Nunito Sans", sans-serif',
                        fontSize: 13,
                        fontWeight: 900,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        color: colors.brown,
                    }}
                >
                    CMS Einstellungen
                </Typography>

                <Typography
                    component="h1"
                    sx={{
                        mb: 1.5,
                        fontFamily: '"Quicksand", sans-serif',
                        fontSize: {
                            xs: "2.35rem",
                            md: "3.5rem",
                        },
                        lineHeight: 0.95,
                        fontWeight: 800,
                        letterSpacing: "-0.06em",
                        color: colors.ink,
                    }}
                >
                    Website-Hinweis
                </Typography>

                <Typography
                    sx={{
                        maxWidth: 760,
                        fontFamily: '"Nunito Sans", sans-serif',
                        fontSize: 16,
                        lineHeight: 1.75,
                        color: colors.muted,
                    }}
                >
                    Aktivieren Sie bei Bedarf einen Hinweis auf der Startseite, zum
                    Beispiel für Sonderöffnungszeiten, neue Ware oder Betriebsferien.
                </Typography>
            </Paper>

            {successMessage && (
                <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
                    {successMessage}
                </Alert>
            )}

            {errorMessage && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
                    {errorMessage}
                </Alert>
            )}

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        lg: "1fr 0.75fr",
                    },
                    gap: 3,
                }}
            >
                <Paper sx={cardStyles} elevation={0}>
                    <Typography sx={cardTitleStyles}>Inhalt</Typography>

                    <Stack spacing={2.2}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.active}
                                    disabled={isSubmitting}
                                    onChange={(event) =>
                                        updateField(
                                            "active",
                                            event.target.checked
                                        )
                                    }
                                    sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": {
                                            color: colors.success,
                                        },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                            {
                                                backgroundColor:
                                                colors.success,
                                            },
                                    }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        fontFamily:
                                            '"Nunito Sans", sans-serif',
                                        fontWeight: 900,
                                        color: form.active
                                            ? colors.success
                                            : colors.muted,
                                    }}
                                >
                                    {form.active
                                        ? "Hinweis ist aktiv"
                                        : "Hinweis ist deaktiviert"}
                                </Typography>
                            }
                        />

                        <FormControl fullWidth sx={fieldStyles}>
                            <InputLabel>Art des Hinweises</InputLabel>
                            <Select
                                label="Art des Hinweises"
                                value={form.variant}
                                disabled={isSubmitting}
                                onChange={(event) =>
                                    updateField("variant", event.target.value)
                                }
                            >
                                <MenuItem value="info">Info</MenuItem>
                                <MenuItem value="important">Wichtig</MenuItem>
                                <MenuItem value="season">Saison</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            label="Titel"
                            value={form.title}
                            onChange={(event) =>
                                updateField("title", event.target.value)
                            }
                            fullWidth
                            required
                            disabled={isSubmitting}
                            sx={fieldStyles}
                        />

                        <TextField
                            label="Nachricht"
                            value={form.message}
                            onChange={(event) =>
                                updateField("message", event.target.value)
                            }
                            multiline
                            minRows={5}
                            fullWidth
                            disabled={isSubmitting}
                            helperText="Kurz und klar formulieren. Dieser Text erscheint öffentlich auf der Website."
                            sx={fieldStyles}
                        />

                        <TextField
                            label="Button-Text optional"
                            value={form.button_label}
                            onChange={(event) =>
                                updateField("button_label", event.target.value)
                            }
                            fullWidth
                            disabled={isSubmitting}
                            helperText="Zum Beispiel: Mehr erfahren"
                            sx={fieldStyles}
                        />

                        <TextField
                            label="Button-Link optional"
                            value={form.button_url}
                            onChange={(event) =>
                                updateField("button_url", event.target.value)
                            }
                            fullWidth
                            disabled={isSubmitting}
                            helperText="Zum Beispiel: /aktuelles oder #kontakt"
                            sx={fieldStyles}
                        />
                    </Stack>
                </Paper>

                <Paper sx={cardStyles} elevation={0}>
                    <Typography sx={cardTitleStyles}>Vorschau</Typography>

                    <Box
                        sx={{
                            p: 2.5,
                            borderRadius: 4,
                            backgroundColor: "rgba(255,255,255,0.62)",
                            border: `1px solid ${colors.border}`,
                        }}
                    >
                        <Typography
                            sx={{
                                mb: 1,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: 12,
                                fontWeight: 900,
                                letterSpacing: "0.14em",
                                textTransform: "uppercase",
                                color: colors.brown,
                            }}
                        >
                            {form.active ? "Aktiver Hinweis" : "Deaktiviert"}
                        </Typography>

                        <Typography
                            sx={{
                                mb: 1,
                                fontFamily: '"Quicksand", sans-serif',
                                fontSize: "1.8rem",
                                lineHeight: 1,
                                fontWeight: 800,
                                letterSpacing: "-0.055em",
                                color: colors.ink,
                            }}
                        >
                            {form.title || "Aktueller Hinweis"}
                        </Typography>

                        <Typography
                            sx={{
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: 15,
                                lineHeight: 1.65,
                                color: colors.muted,
                            }}
                        >
                            {form.message ||
                                "Hier erscheint später der Hinweistext."}
                        </Typography>

                        {form.button_label && form.button_url && (
                            <Button
                                type="button"
                                variant="contained"
                                sx={{
                                    mt: 2,
                                    minHeight: 44,
                                    borderRadius: 999,
                                    backgroundColor: colors.brownDark,
                                    fontFamily:
                                        '"Nunito Sans", sans-serif',
                                    fontWeight: 900,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",

                                    "&:hover": {
                                        backgroundColor:
                                        colors.brownDarker,
                                    },
                                }}
                            >
                                {form.button_label}
                            </Button>
                        )}
                    </Box>
                </Paper>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    mt: 3,
                    p: 2.5,
                    borderRadius: 5,
                    backgroundColor: colors.paper,
                    border: `1px solid ${colors.border}`,
                    display: "flex",
                    justifyContent: "flex-end",
                }}
            >
                <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{
                        minHeight: 52,
                        px: 3.5,
                        borderRadius: 999,
                        backgroundColor: colors.brownDark,
                        color: "#fff",
                        fontFamily: '"Nunito Sans", sans-serif',
                        fontSize: 14,
                        fontWeight: 900,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        boxShadow: "0 14px 30px rgba(111,81,54,0.22)",

                        "&:hover": {
                            backgroundColor: colors.brownDarker,
                            boxShadow:
                                "0 18px 38px rgba(111,81,54,0.28)",
                        },

                        "&:disabled": {
                            color: "rgba(255,255,255,0.78)",
                            backgroundColor: "rgba(111,81,54,0.62)",
                        },
                    }}
                >
                    {isSubmitting ? "Speichert..." : "Speichern"}
                </Button>
            </Paper>
        </Box>
    );
}

const cardStyles = {
    p: {
        xs: 3,
        md: 4,
    },
    borderRadius: 5,
    backgroundColor: colors.paper,
    border: `1px solid ${colors.border}`,
    boxShadow: "0 18px 60px rgba(78,54,30,0.08)",
};

const cardTitleStyles = {
    mb: 2.5,
    fontFamily: '"Quicksand", sans-serif',
    fontSize: {
        xs: "1.9rem",
        md: "2.3rem",
    },
    lineHeight: 1,
    fontWeight: 800,
    letterSpacing: "-0.055em",
    color: colors.ink,
};

const fieldStyles = {
    "& .MuiOutlinedInput-root": {
        borderRadius: 3,
        backgroundColor: "rgba(255,255,255,0.72)",
        fontFamily: '"Nunito Sans", sans-serif',

        "& fieldset": {
            borderColor: "rgba(42,38,33,0.14)",
        },

        "&:hover fieldset": {
            borderColor: "rgba(155,118,83,0.45)",
        },

        "&.Mui-focused fieldset": {
            borderColor: colors.brown,
            borderWidth: 2,
        },
    },

    "& .MuiInputLabel-root": {
        fontFamily: '"Nunito Sans", sans-serif',
        fontWeight: 700,
    },

    "& .MuiFormHelperText-root": {
        fontFamily: '"Nunito Sans", sans-serif',
    },
};

export default AdminSiteNoticeSettings;